import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Heart, Sparkles, ShieldCheck, MessageCircle } from 'lucide-react';
import { DONATE_TIERS, BRAND } from '@/lib/site';

function buildWaLink(amountLabel) {
  const text = encodeURIComponent(
    `Hari Om. I would like to contribute ${amountLabel} to ADI FOUNDATION (Nar Sewa Narayan Sewa). Please share the donation details.`
  );
  // BRAND.whatsappHref is https://wa.me/919990888532
  return `${BRAND.whatsappHref}?text=${text}`;
}

export default function Donate() {
  const [selected, setSelected] = useState(1);
  const tier = DONATE_TIERS[selected];

  return (
    <section
      id="donate"
      data-testid="donate-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      {/* deep gradient bg */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-maroon-700 via-maroon-800 to-[#3F1018]" />
      <div className="absolute inset-0 -z-10 bg-mandala bg-center bg-no-repeat opacity-[0.07] animate-slow-spin" />
      <div className="absolute inset-0 -z-10 grain" />

      <div className="container-x relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left copy */}
          <div className="lg:col-span-5 text-saffron-50">
            <p className="eyebrow text-saffron-300">Donate · 80G eligible</p>
            <h2 className="h-display mt-4 text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05]">
              Your generosity is
              <br />
              <span className="italic font-serif text-saffron-300">someone&rsquo;s tomorrow.</span>
            </h2>
            <p className="mt-6 text-saffron-100/80 text-lg leading-relaxed max-w-md">
              For every rupee you give, 88 paise reaches a child, a grandmother, a flood-hit
              village. The remaining 12 paise keeps our work auditable and honest.
            </p>

            {/* Logo emblem */}
            <div className="mt-10 flex items-center gap-4">
              <div className="grid h-16 w-16 flex-none place-items-center rounded-2xl bg-cream/95 p-1 shadow-[0_8px_28px_-8px_rgba(255,103,31,0.55)]">
                <img
                  src={BRAND.logo}
                  alt=""
                  className="h-full w-full object-contain mix-blend-multiply"
                />
              </div>
              <div>
                <p className="font-display text-2xl">ADI FOUNDATION</p>
                <p className="font-devanagari text-saffron-300 text-sm">नर सेवा नारायण सेवा</p>
              </div>
            </div>

            <div className="mt-10 space-y-4">
              {[
                { icon: ShieldCheck, t: 'Audited every year', s: 'Reports openly published.' },
                { icon: MessageCircle, t: 'Talk to us first', s: 'Every donor speaks to a real human on WhatsApp.' },
                { icon: Sparkles, t: 'Geo-tagged impact', s: 'Photos & stories from the ground.' },
              ].map((b) => (
                <div key={b.t} className="flex gap-3">
                  <div className="grid h-9 w-9 flex-none place-items-center rounded-full bg-saffron-500/20 text-saffron-200">
                    <b.icon className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="font-semibold">{b.t}</p>
                    <p className="text-sm text-saffron-100/70">{b.s}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right donate panel */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 rounded-3xl bg-cream/95 dark:bg-[#22141B]/95 backdrop-blur-xl p-7 md:p-9 border border-saffron-500/20 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.5)]"
          >
            <div>
              <h3 className="h-display text-2xl md:text-3xl font-semibold text-maroon-700 dark:text-saffron-100">
                Choose your seva
              </h3>
              <p className="text-sm text-foreground/60">
                Pick an amount that feels right. We&rsquo;ll guide the rest on WhatsApp.
              </p>
            </div>

            {/* Tiers */}
            <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {DONATE_TIERS.map((t, i) => {
                const active = selected === i;
                return (
                  <button
                    type="button"
                    key={t.amount}
                    data-testid={`donate-tier-${i}`}
                    onClick={() => setSelected(i)}
                    className={`group text-left rounded-2xl border-2 p-5 transition-all ${
                      active
                        ? 'border-saffron-500 bg-saffron-50 dark:bg-saffron-500/10 shadow-[0_18px_40px_-18px_rgba(255,103,31,0.6)]'
                        : 'border-border hover:border-saffron-500/40 bg-card'
                    }`}
                  >
                    <div className="flex items-baseline justify-between">
                      <span className="font-display text-3xl font-bold text-maroon-700 dark:text-saffron-200">
                        {t.amount}
                      </span>
                    </div>
                    <p className="mt-2 text-sm font-semibold text-foreground">{t.label}</p>
                    <p className="mt-0.5 text-xs uppercase tracking-wider text-saffron-600 dark:text-saffron-300">
                      {t.impact}
                    </p>
                  </button>
                );
              })}
            </div>

            {/* Note */}
            <div className="mt-7 rounded-2xl border border-dashed border-saffron-500/40 bg-saffron-50/60 dark:bg-saffron-500/5 p-5">
              <p className="font-serif text-base text-maroon-700 dark:text-saffron-100">
                Click <strong>Donate</strong> and our team will personally guide you on WhatsApp.
              </p>
              <p className="mt-1 text-sm text-foreground/65">
                Every donation, however small, gets a 80G receipt and reaches a real family.
              </p>
            </div>

            <div className="mt-7">
              <Button
                asChild
                size="lg"
                data-testid="donate-cta"
                className="w-full rounded-full bg-saffron-500 hover:bg-saffron-600 py-6 text-base text-white shadow-[0_18px_40px_-12px_rgba(255,103,31,0.7)]"
              >
                <a
                  href={buildWaLink(tier.amount)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2"
                >
                  <Heart className="h-4 w-4 fill-white" />
                  Donate {tier.amount} via WhatsApp
                </a>
              </Button>
            </div>

            {/* Transparency strip */}
            <div className="mt-8 grid grid-cols-3 gap-4 text-center">
              {[
                { v: '88%', l: 'Direct to programs' },
                { v: '12%', l: 'Compliance & ops' },
                { v: '100%', l: 'Reported back' },
              ].map((s) => (
                <div key={s.l} className="rounded-2xl border border-border/70 bg-card p-3">
                  <div className="font-display text-2xl font-bold saffron-text">{s.v}</div>
                  <div className="mt-1 text-[11px] uppercase tracking-wider text-foreground/60">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
